import { useCallback, useEffect, useRef, useState, useTransition } from 'react'

import { yieldToMain } from '@src/quince-site-common/src/utils/yieldToMain'

/**
 * Optimistic option selection for any radio-style selector group (color, size, etc.).
 *
 * Selection state (`selectedValue`) is owned several levels up and, when it changes,
 * triggers a heavy variant/price/EDD recompute across the PDP. Calling `onSelect`
 * synchronously blocks paint of the selected highlight, inflating INP.
 *
 * This hook surfaces the picked value immediately via `effectiveSelected` (urgent state, so
 * the highlight paints within the interaction), then yields to the browser before running
 * the heavy `onSelect` work — so the optimistic paint lands first. `startTransition` only
 * lowers the priority of the resulting re-render; it does NOT move synchronous JS off the
 * click task, which is why the yield (not the transition alone) is what unblocks the paint.
 * The transition still keeps that follow-up re-render interruptible so it doesn't inflate
 * INP on the next click. Once the upstream `selectedValue` catches up, the optimistic value
 * is cleared so a single source of truth remains.
 *
 * NOTE: `onSelect` runs asynchronously (after a yield). Tests that assert it fired must
 * await (`findBy*` / `waitFor`), not assert synchronously after the click.
 *
 * Reusable across any string-valued selector group (color, size, …): pass that group's
 * `selectedValue` + `onSelect` and feed `effectiveSelected`/`handleSelect` back into its
 * highlight + change handler. Rapid back-to-back selections are debounced to the latest pick —
 * a superseded selection's `onSelect` (and its side effects, e.g. analytics) is skipped, which
 * avoids a wasted recompute and a stale-reconcile flicker that would briefly revert the highlight.
 *
 * `enabled` gates the optimization (Statsig `pdp_optimistic_option_selection`). When false the
 * hook is a transparent passthrough — `handleSelect` calls `onSelect` synchronously and
 * `effectiveSelected` is just `selectedValue` — restoring the exact pre-optimization behavior.
 * All React hooks are still called unconditionally so the Rules of Hooks hold either way.
 */
export function useOptimisticSelection(
  selectedValue: string | undefined,
  onSelect: (value: string) => void,
  enabled = true,
) {
  const [optimisticSelected, setOptimisticSelected] = useState<string>()
  const [, startTransition] = useTransition()

  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Monotonic counter so a deferred task can tell whether a newer selection has superseded it.
  const latestSeqRef = useRef(0)

  // Reconcile once the real selectedValue catches up (or changes upstream).
  useEffect(() => {
    setOptimisticSelected(undefined)
  }, [selectedValue])

  // Returns void (not a Promise) so it drops straight into any `onChange`/`onSelect`
  // expecting `(value: string) => void`; the deferral happens internally.
  const handleSelect = useCallback(
    (value: string) => {
      // Gate off → behave exactly like the original synchronous selector.
      if (!enabled) {
        onSelect(value)
        return
      }
      const seq = ++latestSeqRef.current
      setOptimisticSelected(value)
      void (async () => {
        // Let the optimistic highlight paint before the heavy variant/price recompute.
        await yieldToMain()
        // Bail if unmounted, or if a newer selection has superseded this one — otherwise a
        // stale onSelect would run a wasted recompute and clear the optimistic value, briefly
        // reverting the highlight to the superseded pick (visible flicker on fast back-to-back taps).
        if (!isMountedRef.current || seq !== latestSeqRef.current) return
        startTransition(() => {
          onSelect(value)
        })
      })()
    },
    [onSelect, enabled],
  )

  return { effectiveSelected: enabled ? (optimisticSelected ?? selectedValue) : selectedValue, handleSelect }
}
